import { useCallback, useContext, useEffect, useMemo } from 'react';

import { pollForWindowProperty } from '@cx-modules/utils';

import { MatchesCouponContext } from '../context';

import { useMatchesCouponContext } from './useMatchesCouponContext';

export const useBetslip = ({
  betId,
  betLocation,
  betName,
  betOdds,
  bookieCode,
  marketName,
  subeventName,
}: {
  betId: number;
  betLocation?: string;
  betName?: string;
  betOdds?: string | number;
  bookieCode: string;
  marketName?: string;
  subeventName?: string;
}) => {
  const store = useContext(MatchesCouponContext);
  if (!store) throw new Error('Missing MatchesCouponContext.Provider in the tree');

  const selectedBets = useMatchesCouponContext(state => state.selectedBets);
  const addSelectedBet = useMatchesCouponContext(state => state.addSelectedBet);
  const removeSelectedBet = useMatchesCouponContext(state => state.removeSelectedBet);

  // Query window.ocBetslip on mount ONCE (for OC repub with UK betslip)
  useEffect(() => {
    let mounted = true;

    const checkBetslipState = async () => {
      try {
        await pollForWindowProperty('ocBetslip.isInBetslip');

        if (!mounted) return;

        const isInBetslip = window.ocBetslip?.isInBetslip(betId);
        if (isInBetslip) {
          // Bet is in UK betslip - sync it to module state
          addSelectedBet?.({ betId, bookieCode });
        }
      } catch (error) {
        console.warn('[useBetslip] window.ocBetslip.isInBetslip never became available', error);
      }
    };

    checkBetslipState();

    return () => {
      mounted = false;
    };
    // Only run once on mount - don't re-run when selectedBets changes!
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betId, bookieCode]);

  const isBookieBetSelected = useMemo(
    () => selectedBets?.some(bet => bet.betId === betId && bet.bookieCode === bookieCode),
    [betId, selectedBets, bookieCode],
  );

  const toggleBetslipBet = useCallback(() => {
    try {
      window.bettingActions.addOrRemoveFromBetslip(
        [{ betId, selectedBookie: bookieCode }],
        [betId],
        bookieCode,
        betOdds?.toString(),
        betName,
        betLocation,
        subeventName,
        marketName,
        'n/a',
      );
    } catch (e) {
      console.error('useBetslip.toggleBetslipBet', e);
    }

    isBookieBetSelected ? removeSelectedBet?.(betId) : addSelectedBet?.({ betId, bookieCode });
  }, [
    isBookieBetSelected,
    removeSelectedBet,
    betId,
    addSelectedBet,
    bookieCode,
    betOdds,
    betName,
    betLocation,
    subeventName,
    marketName,
  ]);

  return { isBookieBetSelected, toggleBetslipBet };
};
